import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Redirect, useLocation } from "react-router-dom";
import axios from "axios";
import {
  FaCalendarAlt,
  FaEdit,
  FaExchangeAlt,
  FaMoneyBillWave,
  FaSyncAlt,
  FaTrashAlt,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import BackButton from "../ChildComponent/BackButton";
import MdlSelectSession from "../dialog/MdlSelectSession";
import MdlEditExpense from "../dialog/MdlEditExpense";
import COLORS from "../resources/colors/COLORS";
import myToast from "../Utils/MyToast";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const ALL = "ALL";

//Created By Cursor
function formatCurrency(amount) {
  const num = parseFloat(amount) || 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

//Created By Cursor
function parseLooseDate(raw) {
  if (!raw) return null;
  const value = String(raw).trim();
  let m = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = value.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

//Created By Cursor
function buildSessionMonths(startRaw, endRaw) {
  const start = parseLooseDate(startRaw);
  const end = parseLooseDate(endRaw);
  if (!start || !end) return [];
  const months = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  if (cursor > last) return [];
  while (cursor <= last) {
    const year = cursor.getFullYear();
    const month = String(cursor.getMonth() + 1).padStart(2, "0");
    months.push(`${year}-${month}`);
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
}

//Created By Cursor
function getCurrentYearMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

//Created By Cursor
function formatDisplayDate(raw) {
  const value = String(raw || "").trim();
  if (!value) return "—";
  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
  return value;
}

//Created By Cursor
function statusPillClass(status) {
  if (status === "Active" || status === "APPROVED") return "em-pill em-pill--ok";
  if (status === "Pending" || status === "PENDING") return "em-pill em-pill--warn";
  return "em-pill em-pill--bad";
}

//Created By Cursor
function statusLabel(status) {
  if (status === "Active") return "Active";
  if (status === "InActive") return "Inactive";
  return status || "Pending";
}

//Created By Cursor
function readQueryParam(search, key, fallback = "") {
  try {
    const params = new URLSearchParams(search || "");
    const value = params.get(key);
    return value != null && value !== "" ? value : fallback;
  } catch (e) {
    return fallback;
  }
}

//Created By Cursor
export default function ExpenseList() {
  const loggedIn = localStorage.getItem("token") != null;
  const location = useLocation();

  const entryType = String(
    readQueryParam(location.search, "entry_type", "CASH_OUT") || "CASH_OUT"
  ).toUpperCase();
  const urlMonth = readQueryParam(location.search, "year_month", "");

  const [isOpenSessionDialog, setIsOpenSessionDialog] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [sessionStartDate, setSessionStartDate] = useState("");
  const [sessionEndDate, setSessionEndDate] = useState("");
  const [yearMonth, setYearMonth] = useState(urlMonth || ALL);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({
    count: 0,
    total_amount: "0",
    total_cash_out: "0",
    total_cash_in: "0",
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [deletingSelected, setDeletingSelected] = useState(false);
  const [editEntry, setEditEntry] = useState(null);

  const monthOptions = useMemo(
    () => buildSessionMonths(sessionStartDate, sessionEndDate).slice().reverse(),
    [sessionStartDate, sessionEndDate]
  );

  const currentYm = useMemo(() => getCurrentYearMonth(), []);

  const listTotal = useMemo(
    () => rows.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0),
    [rows]
  );

  const rowIdList = useMemo(
    () =>
      rows
        .map((row) => String(row?.entry_id || "").trim())
        .filter(Boolean),
    [rows]
  );

  const allSelected =
    rowIdList.length > 0 && rowIdList.every((id) => selectedIds.includes(id));
  const someSelected =
    selectedIds.length > 0 && selectedIds.some((id) => rowIdList.includes(id));
  const selectedCount = selectedIds.filter((id) => rowIdList.includes(id))
    .length;

  //Created By Cursor
  const fetchList = useCallback(
    async ({ soft = false } = {}) => {
      const schoolCode = SessionManager.getSchoolCode();
      if (!sessionId || !schoolCode) return;

      if (soft) setRefreshing(true);
      else setLoading(true);

      try {
        const params = {
          school_code: schoolCode,
          session_id: sessionId,
          entry_type: entryType || "CASH_OUT",
          category_id: ALL,
          party_id: ALL,
        };
        if (yearMonth && yearMonth !== ALL) {
          params.year_month = yearMonth;
        }

        const res = await axios.get(
          `${API_BASE()}getDaybookExpenseFiltered.php`,
          { params }
        );

        if (res.data?.api_status === "OK") {
          setRows(Array.isArray(res.data.data) ? res.data.data : []);
          setSelectedIds([]);
          setSummary({
            count: Number(res.data.summary?.count) || 0,
            total_amount: res.data.summary?.total_amount || "0",
            total_cash_out: res.data.summary?.total_cash_out || "0",
            total_cash_in: res.data.summary?.total_cash_in || "0",
          });
        } else {
          setRows([]);
          setSelectedIds([]);
          setSummary({
            count: 0,
            total_amount: "0",
            total_cash_out: "0",
            total_cash_in: "0",
          });
          if (res.data?.api_remark) {
            myToast.notify_warning(res.data.api_remark);
          }
        }
      } catch (error) {
        console.error("[ExpenseList] fetch error", error);
        setRows([]);
        setSelectedIds([]);
        myToast.notify_warning("Failed to load expense list.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [sessionId, entryType, yearMonth]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    const mSession = SessionManager.getCurrentSession();
    if (Array.isArray(mSession) && mSession.length > 0) {
      setSessionId(mSession[0].session_id);
      setSessionName(mSession[0].session_name);
      setSessionStartDate(mSession[0].start_date || "");
      setSessionEndDate(mSession[0].end_date || "");

      if (!urlMonth) {
        const months = buildSessionMonths(
          mSession[0].start_date || "",
          mSession[0].end_date || ""
        );
        const current = getCurrentYearMonth();
        if (months.includes(current)) {
          setYearMonth(current);
        }
      }
    }
  }, [urlMonth]);

  useEffect(() => {
    if (!sessionId) return;
    fetchList();
  }, [sessionId, entryType, yearMonth, fetchList]);

  //Created By Cursor
  const toggleSelectId = (entryId) => {
    const id = String(entryId || "").trim();
    if (!id) return;
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  //Created By Cursor
  const toggleSelectAll = () => {
    if (allSelected) setSelectedIds([]);
    else setSelectedIds(rowIdList);
  };

  //Created By Cursor
  const deleteEntriesBulk = async (ids, staffId, schoolCode) => {
    const entryIds = (ids || [])
      .map((id) => String(id || "").trim())
      .filter(Boolean);
    if (entryIds.length === 0) {
      return { ok: false, deleted: 0, remark: "No entries selected." };
    }
    const res = await axios.post(
      `${API_BASE()}deleteDayBookEntriesBulk.php`,
      {
        staff_id: staffId,
        session_id: sessionId,
        school_code: schoolCode,
        entry_ids: entryIds,
      },
      {
        headers: { "Content-Type": "application/json" },
        timeout: 60000,
      }
    );
    return {
      ok: res.data?.api_status === "OK",
      deleted: Number(res.data?.deleted) || 0,
      requested: Number(res.data?.requested) || entryIds.length,
      remark: res.data?.api_remark,
    };
  };

  //Created By Cursor — single delete uses same bulk API (1 id)
  const deleteOneEntry = async (item, staffId, schoolCode) => {
    const entryId = String(item?.entry_id || "").trim();
    if (!entryId) return { ok: false, entryId };
    const result = await deleteEntriesBulk([entryId], staffId, schoolCode);
    return {
      ok: result.ok,
      entryId,
      remark: result.remark,
    };
  };

  //Created By Cursor
  const handleDeleteEntry = async (item) => {
    const entryId = String(item?.entry_id || "").trim();
    if (!entryId || !sessionId) return;

    const staff = SessionManager.getStaffDetails();
    const staffId = staff?.staff_id || "";
    const schoolCode = SessionManager.getSchoolCode();
    if (!staffId || !schoolCode) {
      myToast.notify_warning("Staff session is missing. Please login again.");
      return;
    }

    const amountLabel = formatCurrency(item.amount);
    const dateLabel = formatDisplayDate(item.entry_date);
    const ok = window.confirm(
      `Delete this entry?\n\nDate: ${dateLabel}\nAmount: ${amountLabel}\nCategory: ${
        item.category_name || "—"
      }`
    );
    if (!ok) return;

    setDeletingId(entryId);
    try {
      const result = await deleteOneEntry(item, staffId, schoolCode);
      if (result.ok) {
        myToast.notify_success("Entry deleted.");
        await fetchList({ soft: true });
      } else {
        myToast.notify_warning(result.remark || "Could not delete entry.");
      }
    } catch (error) {
      console.error("[ExpenseList] delete error", error);
      myToast.notify_warning("Failed to delete entry.");
    } finally {
      setDeletingId("");
    }
  };

  //Created By Cursor
  const handleDeleteSelected = async () => {
    if (!sessionId || selectedCount === 0) return;

    const staff = SessionManager.getStaffDetails();
    const staffId = staff?.staff_id || "";
    const schoolCode = SessionManager.getSchoolCode();
    if (!staffId || !schoolCode) {
      myToast.notify_warning("Staff session is missing. Please login again.");
      return;
    }

    const ids = selectedIds.filter((id) => rowIdList.includes(id));
    if (ids.length === 0) return;

    const ok = window.confirm(
      `Delete ${ids.length} selected entr${
        ids.length === 1 ? "y" : "ies"
      }?\n\nThis cannot be undone.`
    );
    if (!ok) return;

    setDeletingSelected(true);
    try {
      const result = await deleteEntriesBulk(ids, staffId, schoolCode);
      if (result.ok) {
        myToast.notify_success(
          `${result.deleted || ids.length} entr${
            (result.deleted || ids.length) === 1 ? "y" : "ies"
          } deleted.`
        );
        await fetchList({ soft: true });
      } else {
        myToast.notify_warning(
          result.remark || "Could not delete selected entries."
        );
      }
    } catch (error) {
      console.error("[ExpenseList] bulk delete error", error);
      myToast.notify_warning("Failed to delete selected entries.");
    } finally {
      setDeletingSelected(false);
    }
  };

  if (!loggedIn) return <Redirect to="/Login" />;

  const titleLabel =
    entryType === "CASH_IN"
      ? "Cash in list"
      : "Expense list";

  return (
    <div
      className="content payroll-page sm-page sr-page stur-page em-page elist-page"
      style={{ background: COLORS.white, fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> {titleLabel}</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell em-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaMoneyBillWave />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">{titleLabel}</h1>
              <p className="sm-hero__sub">
                {summary.count} entries · {formatCurrency(summary.total_amount)}
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              className="stur-hero-session"
              onClick={() => setIsOpenSessionDialog(true)}
              title="Change academic session"
            >
              <FaCalendarAlt aria-hidden />
              <span className="stur-hero-session__text min-w-0">
                <span className="stur-hero-session__label">Session</span>
                <strong className="stur-hero-session__name">
                  {sessionName || "Select session"}
                </strong>
              </span>
              <FaExchangeAlt className="stur-hero-session__swap" aria-hidden />
            </button>
            <button
              type="button"
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              title="Refresh"
              aria-label="Refresh"
              disabled={!sessionId || loading || refreshing}
              onClick={() => fetchList({ soft: true })}
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="sr-body stur-body em-body elist-body">
          <section className="elist-months" aria-label="Select month">
            <div className="elist-months__head">
              <h2 className="elist-months__title">
                <FaCalendarAlt aria-hidden /> Month
              </h2>
              <p className="elist-months__hint">
                {yearMonth === ALL
                  ? "All months"
                  : yearMonth.replace("-", " · ")}
              </p>
            </div>
            {monthOptions.length > 0 ? (
              <div className="elist-months__grid" role="listbox">
                <button
                  type="button"
                  role="option"
                  aria-selected={yearMonth === ALL}
                  className={`elist-month elist-month--all${
                    yearMonth === ALL ? " is-active" : ""
                  }`}
                  onClick={() => setYearMonth(ALL)}
                >
                  <span className="elist-month__name">All</span>
                  <span className="elist-month__year">Month</span>
                </button>
                {monthOptions.map((ym) => {
                  const date = new Date(`${ym}-01T00:00:00`);
                  const monthShort = date.toLocaleString("en-GB", {
                    month: "short",
                  });
                  const yearLabel = String(date.getFullYear());
                  const isCurrent = ym === currentYm;
                  const isActive = yearMonth === ym;
                  return (
                    <button
                      key={ym}
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      className={`elist-month${isActive ? " is-active" : ""}${
                        isCurrent ? " is-current" : ""
                      }`}
                      onClick={() => setYearMonth(ym)}
                      title={`${monthShort} ${yearLabel}`}
                    >
                      {isCurrent ? (
                        <span className="elist-month__badge">Now</span>
                      ) : null}
                      <span className="elist-month__name">{monthShort}</span>
                      <span className="elist-month__year">{yearLabel}</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="elist-months__empty">
                No months available for this session.
              </div>
            )}
          </section>

          <section className="em-panel elist-panel" aria-label="Expense entries">
            <div className="elist-panel__head">
              <div className="min-w-0">
                <h2 className="elist-panel__title">Entries</h2>
              </div>
              <div className="elist-panel__meta">
                {selectedCount > 0 ? (
                  <button
                    type="button"
                    className="elist-del-btn elist-del-btn--bulk"
                    disabled={deletingSelected || !!deletingId}
                    onClick={handleDeleteSelected}
                  >
                    <FaTrashAlt aria-hidden />
                    <span>
                      {deletingSelected
                        ? "Deleting…"
                        : `Delete selected (${selectedCount})`}
                    </span>
                  </button>
                ) : null}
                <span className="elist-meta-pill">
                  {rows.length} shown
                </span>
                <span className="elist-meta-pill elist-meta-pill--total">
                  {formatCurrency(listTotal)}
                </span>
              </div>
            </div>

            {loading ? (
              <div className="em-loading">
                <MyLoader />
              </div>
            ) : rows.length === 0 ? (
              <div className="em-empty">
                <p className="em-empty__title">No entries found</p>
                <p className="em-empty__hint">
                  No daybook entries for the selected month.
                </p>
              </div>
            ) : (
              <div className="em-table-wrap elist-table-wrap">
                <table className="em-table elist-table">
                  <thead>
                    <tr>
                      <th className="elist-col-check">
                        <input
                          type="checkbox"
                          className="elist-check"
                          checked={allSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = someSelected && !allSelected;
                          }}
                          onChange={toggleSelectAll}
                          disabled={deletingSelected || rows.length === 0}
                          aria-label="Select all entries"
                          title="Select all"
                        />
                      </th>
                      <th className="elist-col-sr">#</th>
                      <th className="elist-col-date">Date</th>
                      <th className="elist-col-type">Type</th>
                      <th>Category</th>
                      <th>Party</th>
                      <th>Mode</th>
                      <th>Remark</th>
                      <th className="elist-col-amt">Amount</th>
                      <th className="elist-col-status">Status</th>
                      <th className="elist-col-action">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((item, index) => {
                      const etype = String(item.entry_type || "").toUpperCase();
                      const isIn = etype === "CASH_IN";
                      const entryId = String(item.entry_id || "");
                      const isDeleting = deletingId === entryId;
                      const isChecked = selectedIds.includes(entryId);
                      return (
                        <tr
                          key={entryId || index}
                          className={isChecked ? "is-selected" : undefined}
                        >
                          <td className="elist-col-check">
                            <input
                              type="checkbox"
                              className="elist-check"
                              checked={isChecked}
                              disabled={
                                !entryId || deletingSelected || !!deletingId
                              }
                              onChange={() => toggleSelectId(entryId)}
                              aria-label={`Select entry ${index + 1}`}
                            />
                          </td>
                          <td className="elist-col-sr">{index + 1}</td>
                          <td className="elist-col-date">
                            {formatDisplayDate(item.entry_date)}
                          </td>
                          <td className="elist-col-type">
                            <span
                              className={`elist-type${
                                isIn ? " elist-type--in" : " elist-type--out"
                              }`}
                            >
                              {isIn ? "In" : "Out"}
                            </span>
                          </td>
                          <td>
                            <span className="elist-cell-strong">
                              {item.category_name || "—"}
                            </span>
                          </td>
                          <td>{item.party_name || "—"}</td>
                          <td>
                            <span className="elist-mode">
                              {item.payment_mode || "—"}
                            </span>
                          </td>
                          <td className="elist-remark" title={item.remark || ""}>
                            {item.remark || "—"}
                          </td>
                          <td className="em-table__amount elist-col-amt">
                            {formatCurrency(item.amount)}
                          </td>
                          <td className="elist-col-status">
                            <span className={statusPillClass(item.status)}>
                              {statusLabel(item.status)}
                            </span>
                          </td>
                          <td className="elist-col-action">
                            <div className="elist-actions">
                              <button
                                type="button"
                                className="elist-edit-btn"
                                title="Edit entry"
                                aria-label="Edit entry"
                                disabled={
                                  !!deletingId || deletingSelected || !entryId
                                }
                                onClick={() => setEditEntry(item)}
                              >
                                <FaEdit aria-hidden />
                                <span>Edit</span>
                              </button>
                              <button
                                type="button"
                                className="elist-del-btn"
                                title="Delete entry"
                                aria-label="Delete entry"
                                disabled={
                                  isDeleting || !!deletingId || deletingSelected
                                }
                                onClick={() => handleDeleteEntry(item)}
                              >
                                <FaTrashAlt aria-hidden />
                                <span>{isDeleting ? "…" : "Delete"}</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={8} className="elist-foot-label">
                        Total
                      </td>
                      <td className="em-table__amount elist-col-amt">
                        {formatCurrency(listTotal)}
                      </td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>

      {isOpenSessionDialog ? (
        <MdlSelectSession
          action={() => setIsOpenSessionDialog(false)}
          action2={(id, name, _status, startDate, endDate) => {
            setSessionId(id);
            setSessionName(name);
            setSessionStartDate(startDate || "");
            setSessionEndDate(endDate || "");
            setIsOpenSessionDialog(false);
          }}
        />
      ) : null}

      <MdlEditExpense
        isOpen={!!editEntry}
        onClose={() => setEditEntry(null)}
        entry={editEntry}
        sessionId={sessionId}
        sessionStartDate={sessionStartDate}
        sessionEndDate={sessionEndDate}
        onSuccess={() => fetchList({ soft: true })}
      />
    </div>
  );
}
